import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { CaseOrderHistory, PushBackSkipOffer, WinePlanTypes } from 'tsconfig/types';

interface WinePlanState {
  upcomingCase: WinePlanTypes | undefined;
  caseOrderHistory: CaseOrderHistory | undefined;
  pushBackSkipOffer: PushBackSkipOffer | undefined;
  userDeliveryAddress: { isLoading: boolean; data: any[] };
  userSelectedAddress: { isLoading: boolean; hasError: boolean; data: any };
  pincodeSearch: { isLoading: boolean; hasError: boolean; data: any };
  updateDeliveryAddress: { isLoading: boolean; hasError: boolean; isAddressSaved: boolean };
  useDeliveryAddress: { isLoading: boolean; hasError: boolean; isAddressUpdated: boolean; selectedAddressId: string };
  shopPincodeSearch: { isLoading: boolean; hasError: boolean; data: any };
  useStoreDeliveryAddress: { isLoading: boolean; hasError: boolean; isAddressUpdated: boolean; selectedAddressId: string };
  availableCaseOptions: { isLoading: boolean; hasError: boolean; caseOptions: any[]; caseDetails: any };
  swapSubscription: { isLoading: boolean; hasError: boolean; isSubscriptionSwapped: boolean };
  flexSubscription: { isLoading: boolean; hasError: boolean; isWinePlanModified: boolean };
  isWinePlanFlexed: boolean;
  isCancelSaveOfferApplied: boolean;
}

interface WinePlanActions {
  setUpcomingCase: (data: WinePlanTypes | undefined) => void;
  setCaseOrderHistory: (data: CaseOrderHistory | undefined) => void;
  setPushBackSkipOffer: (data: PushBackSkipOffer | undefined) => void;
  setUserDeliveryAddress: (data: Partial<WinePlanState['userDeliveryAddress']>) => void;
  setUserSelectedAddress: (data: Partial<WinePlanState['userSelectedAddress']>) => void;
  setPincodeSearch: (data: Partial<WinePlanState['pincodeSearch']>) => void;
  setUpdateDeliveryAddress: (data: Partial<WinePlanState['updateDeliveryAddress']>) => void;
  setUseDeliveryAddress: (data: Partial<WinePlanState['useDeliveryAddress']>) => void;
  setShopPincodeSearch: (data: Partial<WinePlanState['shopPincodeSearch']>) => void;
  setUseStoreDeliveryAddress: (data: Partial<WinePlanState['useStoreDeliveryAddress']>) => void;
  setAvailableCaseOptions: (data: Partial<WinePlanState['availableCaseOptions']>) => void;
  setSwapSubscription: (data: Partial<WinePlanState['swapSubscription']>) => void;
  setFlexSubscription: (data: Partial<WinePlanState['flexSubscription']>) => void;
  setIsWinePlanFlexed: (value: boolean) => void;
  setIsCancelSaveOfferApplied: (value: boolean) => void;
  reset: () => void;
}

export type WinePlanStore = WinePlanState & WinePlanActions;

const initialState: WinePlanState = {
  upcomingCase: undefined,
  caseOrderHistory: undefined,
  pushBackSkipOffer: undefined,
  userDeliveryAddress: { isLoading: false, data: [] },
  userSelectedAddress: { isLoading: false, hasError: false, data: null },
  pincodeSearch: { isLoading: false, hasError: false, data: null },
  updateDeliveryAddress: { isLoading: false, hasError: false, isAddressSaved: false },
  useDeliveryAddress: { isLoading: false, hasError: false, isAddressUpdated: false, selectedAddressId: '' },
  shopPincodeSearch: { isLoading: false, hasError: false, data: null },
  useStoreDeliveryAddress: { isLoading: false, hasError: false, isAddressUpdated: false, selectedAddressId: '' },
  availableCaseOptions: { isLoading: false, hasError: false, caseOptions: [], caseDetails: null },
  swapSubscription: { isLoading: false, hasError: false, isSubscriptionSwapped: false },
  flexSubscription: { isLoading: false, hasError: false, isWinePlanModified: false },
  isWinePlanFlexed: false,
  isCancelSaveOfferApplied: false,
};

export const useWinePlanStore = create<WinePlanStore>()(
  devtools(
    (set) => ({
      ...initialState,
      setUpcomingCase: (data) => set({ upcomingCase: data }, undefined, 'setUpcomingCase'),
      setCaseOrderHistory: (data) => set({ caseOrderHistory: data }, undefined, 'setCaseOrderHistory'),
      setPushBackSkipOffer: (data) => set({ pushBackSkipOffer: data }, undefined, 'setPushBackSkipOffer'),
      setUserDeliveryAddress: (data) => set((s) => ({ userDeliveryAddress: { ...s.userDeliveryAddress, ...data } }), undefined, 'setUserDeliveryAddress'),
      setUserSelectedAddress: (data) => set((s) => ({ userSelectedAddress: { ...s.userSelectedAddress, ...data } }), undefined, 'setUserSelectedAddress'),
      setPincodeSearch: (data) => set((s) => ({ pincodeSearch: { ...s.pincodeSearch, ...data } }), undefined, 'setPincodeSearch'),
      setUpdateDeliveryAddress: (data) => set((s) => ({ updateDeliveryAddress: { ...s.updateDeliveryAddress, ...data } }), undefined, 'setUpdateDeliveryAddress'),
      setUseDeliveryAddress: (data) => set((s) => ({ useDeliveryAddress: { ...s.useDeliveryAddress, ...data } }), undefined, 'setUseDeliveryAddress'),
      setShopPincodeSearch: (data) => set((s) => ({ shopPincodeSearch: { ...s.shopPincodeSearch, ...data } }), undefined, 'setShopPincodeSearch'),
      setUseStoreDeliveryAddress: (data) => set((s) => ({ useStoreDeliveryAddress: { ...s.useStoreDeliveryAddress, ...data } }), undefined, 'setUseStoreDeliveryAddress'),
      setAvailableCaseOptions: (data) => set((s) => ({ availableCaseOptions: { ...s.availableCaseOptions, ...data } }), undefined, 'setAvailableCaseOptions'),
      setSwapSubscription: (data) => set((s) => ({ swapSubscription: { ...s.swapSubscription, ...data } }), undefined, 'setSwapSubscription'),
      setFlexSubscription: (data) => set((s) => ({ flexSubscription: { ...s.flexSubscription, ...data } }), undefined, 'setFlexSubscription'),
      setIsWinePlanFlexed: (value) => set({ isWinePlanFlexed: value }, undefined, 'setIsWinePlanFlexed'),
      setIsCancelSaveOfferApplied: (value) => set({ isCancelSaveOfferApplied: value }, undefined, 'setIsCancelSaveOfferApplied'),
      reset: () => set(initialState, undefined, 'reset'),
    }),
    { name: 'WinePlanStore' },
  ),
);

// Selectors
export const selectUpcomingCaseData = (state: WinePlanState) => state.upcomingCase;
export const selectCaseOrderHistoryData = (state: WinePlanState) => state.caseOrderHistory;
